import { CloseSquareTwoTone, DeleteTwoTone } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import React, { type JSX, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationContext } from '@client/context/NotificationContext';
import {
  EAbilityActions,
  type EAbilitySubjects,
} from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useLoading } from '@client/store/loading.store';
import { isDeactivated } from '@client/utils/form.util';
import { logger } from '@client/utils/logger.util';

import { deleteWarning } from './modal.delete';

import type { EStatus, IObjectId } from '@common/types';
import type { ButtonType } from 'antd/es/button';

type TDeactivateAction<
  T extends { id: Required<IObjectId['id']>; status: EStatus },
> = {
  subject: string | keyof typeof EAbilitySubjects;
  entity: T;
  translateKey: string | React.ReactNode;
  type?: ButtonType;
  icon?: JSX.Element;
  isLoading?: boolean;
  showLabel?: boolean;
  disabled?: boolean;
  onDelete?: (entity: T) => void;
};

/**
 * A component that renders a delete button for the given `entityId`.
 * The button is disabled if the user doesn't have enough permissions to delete the given `entityId`.
 * The button is also disabled if the component is in a loading state.
 * When the button is clicked, a confirmation modal will be shown.
 * If the user confirms the deletion, the `onDelete` function will be called with the given `entityId`.
 * @param {TDeactivateAction} props The props for the component
 * @param {IObjectId['id']} props.entityId The id of the entity to be deleted
 * @param {ButtonType} props.type The type of button
 * @param {boolean} props.isLoading Whether or not the component should be in a loading state
 * @param {boolean} props.showLabel Whether or not the component should show a label
 * @param {boolean} props.disabled Whether or not the component should be disabled
 * @param {() => void} props.onDelete The callback function to be called when the deletion is confirmed
 * @returns {JSX.Element|null} The JSX element of the component
 */
export const DeactivateAction = <
  T extends {
    id: Required<IObjectId['id']>;
    status: EStatus;
  },
>(
  props: TDeactivateAction<T>,
): JSX.Element | null => {
  const { modalApi } = useContext(NotificationContext);
  const { t } = useTranslation();
  const { ability } = useAuth();
  const { loading } = useLoading();

  const {
    subject,
    entity,
    translateKey,
    icon,
    type = 'primary',
    showLabel = false,
    onDelete,
  } = props;

  if (!entity) {
    logger.error('DeactivateAction: entity is required');
    return null;
  }

  return (
    <Tooltip
      title={
        isDeactivated(entity.status)
          ? t('message.delete.info')
          : t('message.deactivate.info', { entity: translateKey })
      }
    >
      <Button
        disabled={
          isDeactivated(entity.status) ||
          ability.cannot(
            isDeactivated(entity.status)
              ? EAbilityActions.DELETE
              : EAbilityActions.DEACTIVATE,
            subject,
          )
        }
        loading={loading}
        danger
        type={type}
        icon={
          icon || isDeactivated(entity.status) ? (
            <DeleteTwoTone twoToneColor="#eb2f96" />
          ) : (
            <CloseSquareTwoTone twoToneColor="#eb2f96" />
          )
        }
        onClick={() =>
          deleteWarning({
            status: entity.status,
            translateKey: translateKey as string,
            onApprove: () => onDelete?.(entity),
            config: { modalApi },
          })
        }
      >
        {showLabel && t('actions.delete')}
      </Button>
    </Tooltip>
  );
};
